/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * A map with one draggable pin.
 *
 * Search gets you near the right place; it does not get you the right place. A
 * start line is a particular side of a particular junction, and no query
 * expresses that — so the pin is how the last fifty metres are set, and it is
 * the only control here that can express them.
 *
 * Three things worth knowing:
 *
 *  • **Coordinates lead, the address follows.** Dragging emits lat/lng straight
 *    away, then looks the address up and emits again. The reverse lookup is a
 *    courtesy: if it fails the pin has still moved and the coordinates are still
 *    right, so a failed lookup must never undo the drag.
 *
 *  • **Debounced at 500ms**, same as the search box. Nominatim asks callers to
 *    stay near one request a second, and a drag fires a continuous stream of
 *    positions.
 *
 *  • **The map answers to the props, not the other way round.** Typing
 *    coordinates by hand, or picking a search result, moves the pin — otherwise
 *    the two controls would show different places and neither would be wrong.
 *
 * TODO: tile.openstreetmap.org's usage policy discourages commercial traffic.
 * Fine for development; this needs a keyed tile provider before real volume.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { reverseGeocode } from '../../athlohive/geocode';

const DEBOUNCE_MS = 500;

/** Bengaluru. Only ever seen before a place is chosen. */
const FALLBACK_CENTER = [12.9716, 77.5946];
const FALLBACK_ZOOM = 11;
const PLACED_ZOOM = 15;

/**
 * Leaflet's default marker points at image files it expects to resolve relative
 * to the CSS. Under a bundler they do not resolve, and the marker renders as a
 * broken image — so the icon is drawn inline instead. No asset, nothing to 404.
 */
const PIN_ICON = L.divIcon({
  className: 'orgf-mappin',
  html: '<span class="orgf-mappin__dot" aria-hidden="true"></span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const isNum = (value) => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value));

export const hasPoint = (point) => isNum(point?.lat) && isNum(point?.lng);

/** Click anywhere to place the pin — quicker than dragging across a city. */
function ClickToPlace({ onPick }) {
  useMapEvents({ click: (event) => onPick(event.latlng.lat, event.latlng.lng) });
  return null;
}

/**
 * Keeps the view with the pin when it moves from OUTSIDE the map — a search
 * result, or typed coordinates. Deliberately not on drag: recentring under
 * somebody's cursor mid-drag fights them for control of the map.
 */
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (!isNum(lat) || !isNum(lng)) return;
    const current = map.getCenter();
    const moved = Math.abs(current.lat - Number(lat)) > 1e-6
      || Math.abs(current.lng - Number(lng)) > 1e-6;
    if (moved) map.setView([Number(lat), Number(lng)], Math.max(map.getZoom(), PLACED_ZOOM));
  }, [lat, lng, map]);
  return null;
}

export default function MapPointPicker({ point, onChange, label, readOnly = false }) {
  const [looking, setLooking] = useState(false);
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const placed = hasPoint(point);
  const center = useMemo(
    () => (placed ? [Number(point.lat), Number(point.lng)] : FALLBACK_CENTER),
    // Only the FIRST center matters — Recenter handles every move after that.
    // Depending on `point` here would rebuild the map on every drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    abortRef.current?.abort();
  }, []);

  const place = useCallback((lat, lng) => {
    // Emitted first and unconditionally: the pin has moved, so the coordinates
    // are already true regardless of what the address lookup goes on to do.
    onChange({ lat: String(lat), lng: String(lng) });

    clearTimeout(timerRef.current);
    abortRef.current?.abort();
    setLooking(true);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      const found = await reverseGeocode(lat, lng, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setLooking(false);
      // No match: keep the coordinates, keep whatever address is already there.
      // Blanking it would lose a correct address because a third party was down.
      if (!found) return;
      onChange({
        lat: String(lat),
        lng: String(lng),
        address: found.label,
        state: found.state || '',
        country: found.country || '',
        pincode: found.pincode || '',
      });
    }, DEBOUNCE_MS);
  }, [onChange]);

  return (
    <div className="orgf-map">
      <MapContainer
        center={center}
        zoom={placed ? PLACED_ZOOM : FALLBACK_ZOOM}
        scrollWheelZoom={false}
        style={{ height: 220, width: '100%', borderRadius: 10 }}
        aria-label={label ? `Map for ${label}` : 'Map'}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/*
          The map still renders when read-only — seeing WHERE a finished event
          started is the main thing left to read off it. What goes is the ability
          to move the pin: no click-to-place, and a marker that does not drag.
        */}
        {!readOnly && <ClickToPlace onPick={place} />}
        {placed && (
          <>
            <Recenter lat={point.lat} lng={point.lng} />
            <Marker
              position={[Number(point.lat), Number(point.lng)]}
              icon={PIN_ICON}
              draggable={!readOnly}
              eventHandlers={readOnly ? undefined : {
                dragend: (event) => {
                  const { lat, lng } = event.target.getLatLng();
                  place(lat, lng);
                },
              }}
            />
          </>
        )}
      </MapContainer>
      <span className="orgf-help">
        {readOnly
          ? (placed ? 'Where this was.' : 'No location was set.')
          : (placed
            ? 'Drag the pin, or click the map, to move it. The address updates to match.'
            : 'Click the map to drop a pin, or search above.')}
        {!readOnly && looking ? ' Looking up the address…' : ''}
      </span>
    </div>
  );
}
